/**
 * This object contains all of the methods used by omniCanvas, as well as a function that can be
 * used to easily expand omniCanvas with more methods
 */
import {configureScope, captureMessage} from '@sentry/vue';
import merge from 'deepmerge';

import {Logger, Vector, SierraCanvas} from '@teemill/common/classes';
import {OmniObject, OmniImage, OmniBounds} from './objects';

import {isBase64, hasTransparency} from '@teemill/common/helpers';
import {isObject, uniqueId} from 'lodash';

import {EventBus} from '@teemill/common/classes';
import {Mathf} from './omnicanvas.utilities';
import {OmniCanvasEvent, EventListeners} from './omnicanvas.events';
import {OmniCanvasGuideline, guidelinePrefabs} from './omnicanvas.guidelines';
import OmniCanvasRenderer from './omnicanvas.renderer';
import OmniFactory from './omnicanvas.factory';

export default class OmniCanvas {
  renderer;

  mousePoint = undefined;
  touchPoints = [];
  mouseState = [];

  /**
   * @type {OmniObject}
   */
  activeEl = undefined;
  animationRequests = [];
  debugMode = false;
  /**
   * @property exportMode - this is set to true when the canvas is being exported
   */
  exportMode = false;
  omniCanvas = null;
  cnv;
  ctx;
  bgImg;

  historyLog = [];
  forwardhistoryLog = [];

  /**
   * @property {OmniFactory} factory The factory is used to create objects. Note
   * that the factory is only required when adding objects dynamically, where you
   * might not know beforehand which class to import. e.g. when loading an
   * omnicanvas JSON template. In order to work, you must register the required
   * classes with the omnicanvas' installations factory.
   *
   * In typical usage, this is not required.
   *
   * @see OmniFactory
   */
  factory = new OmniFactory();

  defaultOptions = {
    /**
     * @var {Float} scale Used to zoom in or out of the canvas
     */
    scale: 1,
    prevScale: 1,

    /**
     * @var {Vector} globalTranslation Translate the entire canvas by x and y
     */
    globalTranslation: new Vector(0, 0),

    /**
     * @var {Number} filterStrength Can be used to reduce the effect of certain filters
     */
    filterStrength: 1,

    /**
     * @var {Object} activeGuidelines Enable or disable guidelines
     */
    activeGuidelines: guidelinePrefabs.studio,

    guidelines: [],

    /**
     * @var {Logger} logger Can be used to attach a custom logger
     * @see logger.js
     */
    logger: null,

    /**
     * @var {String[]} disabledEvents Use this to block event handlers for certain events
     */
    disabledEvents: [],

    /**
     * @var {Object[]} objects The objects currently in the stage
     */
    objects: [],

    /**
     * @var {Object[]} lerpingVars All the active lerpers
     * @see lerp
     */
    lerpingVars: [],

    /**
     * @var {Object} filters All the active global filters on this stage
     */
    filters: {},

    /**
     * @var {Float} baseLuminance The approximate luminance of the background
     */
    baseLuminance: 1,

    // ! All properties above this point are considered private
    // TODO: Remove these from the .params object and prefix with _

    /**
     * Options for the stage
     */
    stage: {
      /**
       * @var {Number} minZoom set a minimum value for the scale property
       */
      minZoom: 1,

      /**
       * @var {Number} maxZoom set a maximum value for the scale property
       */
      maxZoom: 5,

      /**
       * @var {Boolean} animationsEnabled If true, some values may be animated via lerping
       * @see lerp()
       */
      animationsEnabled: true,

      /**
       * @var {Int} width The width of the stage and canvas element
       */
      width: 1000,

      /**
       * @var {Int} height The height of the stage and canvas element
       */
      height: 1000,

      /**
       * @var {Boolean} evented If false, all mouse events, touch events etc. will be
       * ignored
       */
      evented: true,

      /**
       * @var {Boolean} globalControlsEnabled If global controls are enabled, then the
       * entire canvas will be affected by mouse events, rather than individual
       * elements
       */
      globalControlsEnabled: false,

      /**
       * @var {Boolean} fadeOutOfBounds If true, any elements that are outside of the
       * bounds will be faded or altered to indicate they are out of bounds
       */
      fadeOutOfBounds: true,

      /**
       * @var {String} fadeMethod Change how out of bounds elements are faded. Valid
       * values are 'block' and 'alpha'
       */
      fadeMethod: 'block',

      /**
       * @var {Number} fadeAlpha Determines the opacity of out of bounds faded elements
       */
      fadeAlpha: 0.65,

      /**
       * @var {Object[]} layers All the layers in the stage
       */
      layers: [
        {
          tag: 'default',
          sortorder: 1,
          /**
           * @type {OmniBounds} bounds The bounds of the layer
           */
          bounds: null,
        },
      ],

      /**
       * @var {String} activeLayer The name of the current active layer
       */
      activeLayer: null,

      zoomToBounds: true,
      focalPoint: null,
      clearFocalPointOnZoomReset: false,
    },

    history: {
      /**
       * @var {Boolean} enabled If enabled, users will be able to undo changes with Ctrl+Z
       */
      enabled: false,

      /**
       * @var {Number} maximumLogs Determines the maximum amount of times you can press undo
       * 0 is treated as infinite
       */
      maximumLogs: 25,
    },

    /**
     * Options for the background
     */
    background: {
      /**
       * @var {Boolean} imageEnabled Can be used to enable or disable background
       * image layer
       */
      imageEnabled: false,

      /**
       * @var {Boolean} shadow Toggles a shadow effect on the background
       * only valid if a backgroundFill is provided
       */
      shadow: true,

      /**
       * @type {string|null}
       * @var {String} fill A CSS compatible background color
       */
      fill: null,
    },

    foreground: {
      imageEnabled: false,

      opacity: 1,
    },

    /**
     * Generic options for any element type
     *
     * All options provided here can also be overriden in the specific object
     * type properties. For example, you could set text.boundsColor to purple
     * and image.boundsColor to green
     */
    object: {
      /**
       * @var {Boolean} uniformScaling If true, scaling will apply in both the X and Y axis
       * so that an elements aspect ratio is always maintained
       */
      uniformScaling: true,

      /**
       * @var {Boolean} showDimensions If true, the approximate cm dimensions will be
       * displayed above elements
       */
      showDimensions: false,

      /**
       * @var {Boolean} retainPhysicalDimensions Retain dimensions
       */
      retainPhysicalDimensions: false,

      /**
       * @var {String} units One of 'cm' or 'in'
       * @see showDimensions
       */
      units: 'cm',

      /**
       * @var {String} boundsColor A CSS compatible colour string that determines
       * the colour of the objects bounding box
       */
      boundsColor: null,

      /**
       * @var {String} lockedBoundsColor A CSS compatible colour string that
       * determins the colour of the objects bounding box, when the object is locked
       */
      lockedBoundsColor: '#f41',

      lockIconSize: 24,

      /**
       * @var {Boolean} boundsDashed If true, the outer bounding box will appear dashed
       * according to the lineDash property
       */
      boundsDashed: false,

      /**
       * @var {Array} boundsDash An array of numbers that specify distances to
       * alternately draw a line and a gap
       *
       * @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/setLineDash
       */
      boundsLineDash: [12, 10],

      boundsLineWidth: 1,

      /**
       * @var {function} onAfterInitialise Can be used to run an event or modify
       * the object after it is added to the stage
       */
      onAfterInitialise: null,

      perspective: {
        maxFrameRate: 60,
      },
    },

    /**
     * Options for text elements
     */
    text: {
      /**
       * @var {Boolean} enableEmojis If disabled, emojis will be stripped from text
       * elements during render
       */
      enableEmojis: false,

      /**
       * @var {String} defaultFont determines the default font for text elements
       */
      defaultFont: 'Fredoka One',
    },

    /**
     * Options for image elements
     */
    image: {
      /**
       * @var {Boolean} mipMapsEnabled If true, mip maps will be used to improve rendering
       */
      mipMapsEnabled: true,
    },

    bounds: {
      /**
       * @var {Boolean | function} show Enable or disable the bounding box from appearing
       * typically a boolean, but alternatively you can provide a method to use custom
       * logic to determine when to show or not
       */
      show: true,

      /**
       * @var {Object} extra Optional. Can be used to add secondary bounds that
       * only appear when an element is in close proximity. These bounds are for
       * display only and serve no purpose beyond helping a user align their
       * design to a specific area
       */
      extra: null,

      /**
       * @var {Boolean} force If true, the bounds will show even if no other
       * conditions are met
       */
      force: false,

      /**
       * @var {String} color The colour to use for the bounding box border.
       * If no color is provided then the renderer will try to automatically
       * colour the bounds according to the background
       */
      color: null,

      /**
       * @var {Boolean} dashed If true, the outer bounding box will appear dashed
       * according to the lineDash property
       */
      dashed: true,

      /**
       * @var {Array} lineDash An array of numbers that specify distances to
       * alternately draw a line and a gap
       *
       * @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/setLineDash
       */
      lineDash: [12, 10],
    },

    /**
     * Options for snapping elements during rotation/movement
     */
    snapping: {
      /**
       * @var {Int} rotation The distance required before snapping is applied
       */
      rotation: 5,

      /**
       * @var {Int} position The distance required before snapping is applied
       */
      position: 5,
    },

    guideline: {
      /**
       * @var {String} mode One of 'automatic' or 'static'
       *
       * static guidelines will always be visible whenever an item is being dragged
       * in the canvas
       */
      mode: 'automatic',
    },
  };

  /**
   * @param {HtmlCanvasElement} el The canvas element this will render to
   * @param {Object} options The configuration options for this omnicanvas instance
   * @param {Object} withClasses OmniObject class definitions to be passed through
   * to this OmniCanvas' factory
   */
  constructor(el, options, withClasses = null) {
    this.canvasElement = el;
    this.logger = new Logger('#4caf50', 'OmniCanvas');
    this.logger.performanceMonitoring = false;

    this.eventBus = new EventBus();

    if (withClasses) {
      this.factory.registerMultiple(withClasses);
    }

    this.init(options);
  }

  /**
   * instantiate an OmniCanvas object
   *
   * @param {Object} opts A list of options for this instance of OmniCanvas
   * @return {void}
   */
  init(opts) {
    this.params = merge(this.defaultOptions, opts);

    // the Vector must be reconstructed after merge because it is converted
    // to a regular object
    if (this.params.globalTranslation) {
      this.params.globalTranslation = new Vector(
        this.params.globalTranslation.x,
        this.params.globalTranslation.y
      );
    }

    this.renderer = new OmniCanvasRenderer(this);

    this.changeBounds(
      new OmniBounds({
        width: this.params?.stage?.width ?? 100,
        height: this.params?.stage?.height ?? 100,
      })
    );

    this.attachHandlers();
    this.render();

    this.createGuidelines();

    const event = new CustomEvent('canvasInitialised', {
      detail: {canvas: this},
    });
    this.canvasElement.dispatchEvent(event);
  }

  createCanvas(width, height) {
    return new SierraCanvas(width, height);
  }

  createGuidelines() {
    this.params.guidelines = [];

    Object.keys(this.params.activeGuidelines).forEach(guideline => {
      const guidelineRules = this.params.activeGuidelines[guideline];
      this.params.guidelines.push(
        new OmniCanvasGuideline(
          this,
          guidelineRules.direction,
          guidelineRules.position,
          {
            snap: guidelineRules.snap,
            clipping: guidelineRules.clipping,
          }
        )
      );
    });
  }

  updateGuidelinesFromPrefab(...codes) {
    let guidelines = {};

    codes.forEach(code => {
      if (guidelinePrefabs[code]) {
        guidelines = Object.assign(guidelines, guidelinePrefabs[code]);
      }
    });

    this.params.activeGuidelines = guidelines;

    this.createGuidelines();

    this.render();
  }

  /**
   * attach event handlers to OmniCanvas such as click, mousedown, mouseup etc.
   *
   * @return {void}
   */
  attachHandlers() {
    this.listeners = new EventListeners(this);

    this.listeners.attach();
  }

  /**
   * Render a frame
   *
   * @param {Object} renderOptions Options to be passed through to the renderer
   *
   * @return {void}
   */
  render(renderOptions = {}) {
    if (!this.renderer) {
      return;
    }

    this.groupElementsByOverlap();

    this.renderer.render(renderOptions);
  }

  getOverlapBounds(object) {
    const bounds = {
      minX: 0,
      maxX: 0,
      minY: 0,
      maxY: 0,
    };

    if (typeof object.calculateOuterBounds === 'function') {
      const outerBounds = object.outerBounds || object.calculateOuterBounds();
      if (outerBounds) {
        const minX = Math.min(
          outerBounds.tl.x,
          outerBounds.tr.x,
          outerBounds.bl.x,
          outerBounds.br.x
        );
        const maxX = Math.max(
          outerBounds.tl.x,
          outerBounds.tr.x,
          outerBounds.bl.x,
          outerBounds.br.x
        );
        const minY = Math.min(
          outerBounds.tl.y,
          outerBounds.tr.y,
          outerBounds.bl.y,
          outerBounds.br.y
        );
        const maxY = Math.max(
          outerBounds.tl.y,
          outerBounds.tr.y,
          outerBounds.bl.y,
          outerBounds.br.y
        );

        bounds.minX = minX;
        bounds.maxX = maxX;
        bounds.minY = minY;
        bounds.maxY = maxY;
        return bounds;
      }
    }

    const interactionBounds =
      typeof object.getInteractionBounds === 'function'
        ? object.getInteractionBounds()
        : null;
    const width = interactionBounds?.width ?? object.width ?? 0;
    const height = interactionBounds?.height ?? object.height ?? 0;
    const offsetX = interactionBounds?.offsetX ?? 0;
    const offsetY = interactionBounds?.offsetY ?? 0;
    const scale = object.scale ?? 1;
    const halfWidth = (width * scale) / 2;
    const halfHeight = (height * scale) / 2;
    const center = object.centerPoint ? object.centerPoint() : object.position;
    const centerX = (center?.x ?? 0) - offsetX;
    const centerY = (center?.y ?? 0) - offsetY;

    bounds.minX = centerX - halfWidth;
    bounds.maxX = centerX + halfWidth;
    bounds.minY = centerY - halfHeight;
    bounds.maxY = centerY + halfHeight;

    return bounds;
  }

  groupElementsByOverlap() {
    const objects = this.getObjects();

    objects.forEach(object => {
      object.overlappingGroupId = null;
    });

    if (objects.length < 2) {
      return;
    }

    const objectsWithBounds = objects.map(object => ({
      object,
      bounds: this.getOverlapBounds(object),
    }));

    objectsWithBounds.sort((a, b) => a.bounds.minX - b.bounds.minX);

    for (let i = 0; i < objectsWithBounds.length; ++i) {
      const objA = objectsWithBounds[i].object;
      const boundsA = objectsWithBounds[i].bounds;
      for (let j = i + 1; j < objectsWithBounds.length; ++j) {
        const boundsB = objectsWithBounds[j].bounds;
        if (boundsB.minX > boundsA.maxX) {
          break;
        }

        if (boundsB.minY > boundsA.maxY || boundsB.maxY < boundsA.minY) {
          continue;
        }

        const objB = objectsWithBounds[j].object;
        if (objA.overlaps(objB)) {
          let groupId = null;
          if (objA.overlappingGroupId) {
            groupId = objA.overlappingGroupId;
          } else if (objB.overlappingGroupId) {
            groupId = objB.overlappingGroupId;
          } else {
            groupId = uniqueId();
          }
          objA.overlappingGroupId = groupId;
          objB.overlappingGroupId = groupId;
        }
      }
    }
  }

  afterNextRender(callback) {
    this.renderer?.defineRenderCallback(callback);
  }

  export(format, quality, scale = 1, zoom = 1, antiAliasingEnabled = true) {
    const selectedElement = this.deselectActiveElement();

    const result = this.renderer.export(
      format,
      quality,
      scale,
      zoom,
      antiAliasingEnabled
    );

    this.activeEl = selectedElement;

    return result;
  }

  exportAsBlob(
    callback,
    format,
    quality,
    scale = 1,
    zoom = 1,
    antiAliasingEnabled = true
  ) {
    const selectedElement = this.deselectActiveElement();

    const result = this.renderer.exportAsBlob(
      callback,
      format,
      quality,
      scale,
      zoom,
      antiAliasingEnabled
    );

    this.activeEl = selectedElement;

    return result;
  }

  hasValidBounds() {
    return (
      this.boundingBox &&
      this.boundingBox.position.x !== null &&
      this.boundingBox.position.y !== null
    );
  }

  /**
   * Exactly the same as method export, except this will return an object containing all active layers
   *
   * @see export All parameters are the exact same as the export function
   *
   * @return {Object} an object of Base64 Image URLs, or imageData if format = 'imagedata'
   * The keys of this object will be the names of the layers, with the value
   * being the base64 or imagedata representation of the layer
   * If a layer has no contents (stageIsEmpty) then it will not be included
   */
  exportLayers(
    format,
    quality,
    scale,
    zoom,
    antiAliasingEnabled,
    blobCallback
  ) {
    const layers = this.params.stage.layers.sort(
      (a, b) => a.sortorder > b.sortorder
    );
    const base64s = {};
    const startLayer = this.activeLayerName;

    this.params.background.imageEnabled = false;

    layers.forEach(layer => {
      this.setActiveLayer(layer.tag);
      if (!this.stageIsEmpty()) {
        if (blobCallback) {
          base64s[layer.tag] = this.exportAsBlob(
            blobCallback,
            format,
            quality,
            scale,
            zoom,
            antiAliasingEnabled
          );
        } else {
          base64s[layer.tag] = this.export(
            format,
            quality,
            scale,
            zoom,
            antiAliasingEnabled
          );
        }
      }
    });

    this.params.background.imageEnabled = true;

    this.setActiveLayer(startLayer);
    return base64s;
  }

  createScaledCopies(scale, renderOptions) {
    const layers = this.params.stage.layers.sort(
      (a, b) => a.sortorder > b.sortorder
    );
    const scaledCopies = {};
    const startLayer = this.activeLayerName;

    this.params.background.imageEnabled = false;

    layers.forEach(layer => {
      this.setActiveLayer(layer.tag);
      if (!this.stageIsEmpty()) {
        scaledCopies[layer.tag] = this.renderer.createScaledCopy(
          scale,
          renderOptions
        );
      }
    });

    this.params.background.imageEnabled = true;
    this.setActiveLayer(startLayer);

    return scaledCopies;
  }

  /**
   * returns an element at position (x, y)
   *
   * @param {Vector} point the position in the canvas to find an element
   * @param {Array} objects an array of objects to search amongst
   *  if no objects are provided then this will search amongst all objects
   * @return {Object} The OmniCanvas element at position, or null if no
   * element found. Returned result can be anything that extends
   * OmniObject
   */
  findObjectAtPosition(point, objects) {
    if (!objects) {
      objects = this.getObjects(this.activeLayerName);
    }

    if (this.activeEl) {
      const objectAtPosition = this._testObjectAtPosition(
        point,
        this.activeEl,
        true
      );
      if (objectAtPosition) {
        return objectAtPosition;
      }
    }

    for (let i = 0, l = objects.length; i < l; --l) {
      const objectAtPosition = this._testObjectAtPosition(
        point,
        objects[l - 1]
      );
      if (objectAtPosition) {
        return objectAtPosition;
      }
    }

    return null;
  }

  /**
   * Checks if an object or its controls are at a position. Will return the object
   * (OmniObject) or the object's control element (OmniControl).
   *
   * @param {Vector} point The point to test
   * @param {OmniObject} object The object to test
   * @param {Boolean} testObject Should this test the object itself?
   *
   * @return {OmniObject}
   */
  _testObjectAtPosition(point, object, controlsOnly = false) {
    let centerPoint;
    let relativePoint;
    let control;

    if (object.controllable) {
      centerPoint = object.centerPoint();
      relativePoint = new Vector(
        point.x - centerPoint.x,
        point.y - centerPoint.y
      );
      relativePoint = relativePoint.rotate(
        object.angle * (controlsOnly ? -1 : 1)
      );

      control = this.findControlAtPosition(object, relativePoint);
      if (control) {
        return control;
      }

      if (!controlsOnly) {
        const interactionBounds =
          typeof object.getInteractionBounds === 'function'
            ? object.getInteractionBounds()
            : null;
        const boundsWidth = interactionBounds?.width ?? object.width;
        const boundsHeight = interactionBounds?.height ?? object.height;
        const offsetX = interactionBounds?.offsetX ?? 0;
        const offsetY = interactionBounds?.offsetY ?? 0;
        const adjustedPoint = relativePoint.translate(-offsetX, -offsetY);

        if (
          Math.abs(adjustedPoint.x) <= (boundsWidth / 2) * object.scale &&
          Math.abs(adjustedPoint.y) <= (boundsHeight / 2) * object.scale
        ) {
          return object;
        }
      }
    }
    return null;
  }

  /**
   * Same as findObjectAtPosition except it will return a control that exists in an object
   *
   * @param {Object} object The OmniCanvas object to search in
   * @param {Vector} relativePoint The position in the canvas, relative to object, to search at
   *
   * @see OmniObject
   * @see OmniControl
   *
   * @return {OmniControl}
   */
  findControlAtPosition(object, relativePoint) {
    const pixelScale = this.renderer.pixelScale;
    let activeControl = false;
    const interactionBounds =
      typeof object.getInteractionBounds === 'function'
        ? object.getInteractionBounds()
        : null;
    const boundsWidth = interactionBounds?.width ?? object.width;
    const boundsHeight = interactionBounds?.height ?? object.height;
    const padding =
      typeof object.getBoundsPadding === 'function'
        ? object.getBoundsPadding()
        : null;
    const paddingScale = object.scale ?? 1;
    const paddingLeft = (padding?.left ?? 0) * paddingScale;
    const paddingTop = (padding?.top ?? 0) * paddingScale;

    Object.keys(object.controls)
      .filter(control => object.controls[control].layer === object.controlLayer)
      .forEach(j => {
        const control = object.controls[j];
        if (typeof control.getPosition === 'function') {
          control.getPosition(object);
        }
        const centerPoint = object.centerPoint();
        const controlPosition = control.placement
          .copy()
          .multiply(new Vector(boundsWidth, boundsHeight))
          .translate(-paddingLeft, -paddingTop);

        if (control.offset) {
          controlPosition.translate(control.offset.x, control.offset.y);
        }

        control.position = controlPosition;
        control.object = object;

        const relativeControlPosition = controlPosition
          .translate(-centerPoint.x, -centerPoint.y)
          .translate(object.position.x, object.position.y);

        const paddingFactor = control.paddingFactor * pixelScale;

        const halfControlWidth = (control.width * paddingFactor) / 2;
        const halfControlHeight = (control.height * paddingFactor) / 2;

        if (
          relativePoint.x > relativeControlPosition.x - halfControlWidth &&
          relativePoint.x < relativeControlPosition.x + halfControlWidth &&
          relativePoint.y > relativeControlPosition.y - halfControlHeight &&
          relativePoint.y < relativeControlPosition.y + halfControlHeight
        ) {
          activeControl = control;
        }
      });

    return activeControl;
  }

  get canvasCenterPoint() {
    const canvasScale = this.params.scale;
    const bounds = this.boundingBox;
    const globalTranslation = bounds?.fixed
      ? new Vector(0, 0)
      : this.params?.globalTranslation ?? new Vector(0, 0);

    const effectiveHalfCanvasHeight =
      bounds.position.y + globalTranslation.y + bounds.height / 2;
    const effectiveHalfCanvasWidth =
      bounds.position.x + globalTranslation.x + bounds.width / 2;

    return new Vector(
      effectiveHalfCanvasWidth - effectiveHalfCanvasWidth * canvasScale,
      effectiveHalfCanvasHeight - effectiveHalfCanvasHeight * canvasScale
    );
  }

  /**
   * Returns the name of the current active layer
   */
  get activeLayerName() {
    return this.params.stage.activeLayer;
  }

  /**
   * Returns the current active layer object
   */
  get activeLayer() {
    return this.getLayerByName(this.activeLayerName);
  }

  /**
   * Returns the current active bounding box
   */
  get boundingBox() {
    const bounds = this.activeLayer?.bounds;

    if (!bounds) {
      return new OmniBounds({
        position: new Vector(0, 0),
        width: this.params.stage.width,
        height: this.params.stage.height,
      });
    }

    return bounds;
  }

  /**
   * adds an object to omniCanvas
   *
   * @param {OmniObject|OmniText|OmniImage} obj The object to add
   * The object must first be instantiated via 'new Omni<className>()'
   *
   * @return {OmniObject} the new Object in the canvas
   */
  insert(obj) {
    obj.init(this);
    this.params.objects.push(obj);
    this.render();

    if (typeof obj.z === 'undefined') {
      obj.z = this.params.objects.length - 1;
    }

    this.onElementAdded(obj);

    this.commitHistoryLog();

    return obj;
  }

  // ! DEPRECATED
  createObject(obj) {
    this.logger.log(
      'createObject has been deprecated. User insert instead',
      this.logger.flags.ERROR
    );

    this.insert(obj);
  }

  /**
   * Finds an object by id
   *
   * @param {Int} id The id to search by
   *
   * @return {OmniObject} Either an OmniObject or an empty object (if no object was found)
   */
  findObject(id) {
    const objects = this.getObjects();

    for (let i = 0, l = objects.length; i < l; ++i) {
      if (String(objects[i].id) === String(id)) {
        return objects[i];
      }
    }

    return null;
  }

  findObjectsByClassName(className) {
    const objects = this.getObjects();

    return objects.filter(object => object.className === className);
  }

  /**
   * Deletes an object from the canvas
   *
   * @param {OmniObject} obj The object to delete
   * @return {void}
   */
  deleteObject(obj) {
    if (!obj) {
      obj = this.activeEl;
      if (!obj) {
        return;
      }
    }

    obj.onBeforeDelete();

    const objects = this.params.objects;
    const l = objects.length;
    let objIndex = 0;

    for (let i = 0; i < l; ++i) {
      if (objects[i].id === obj.id) {
        objIndex = i;
        break;
      }
    }

    if (this.activeEl && obj.id === this.activeEl.id) {
      this.activeEl = undefined;
      this.onElementDeselected(obj);
    }

    this.params.objects.splice(objIndex, 1);
    this.render();

    this.onElementDeleted(obj);
  }

  /**
   * Deletes all objects from the canvas
   * @return {void}
   */
  deleteObjects() {
    this.params.objects = [];
    this.activeEl = undefined;
    this.render();
  }

  /**
   * replaces the bounds object with another
   *
   * @param {OmniBounds} bounds the new Bounds
   * @return {void}
   */
  updateBounds(bounds) {
    if (this.validateObject(bounds, ['position', 'width', 'height'])) {
      this.renderer.drawBounds();
      this.setLayerBounds(this.activeLayerName, bounds);
    } else {
      // eslint-disable-next-line
      console.error('invalid bounds object');
    }
  }

  updateDimensions(width, height) {
    this.params.stage.width = width;
    this.params.stage.height = height;

    this.renderer.updateDimensions(width, height);
  }

  /**
   * check that an object contains all required fields
   *
   * @param {Object} object The object to validate
   * @param {Array} requiredFields An array of fields to check
   *
   * @return {Boolean} returns false if one or more the requiredFields does not exist in object
   */
  validateObject(object, requiredFields) {
    for (let i = 0, l = requiredFields.length; i < l; ++i) {
      if (typeof object[requiredFields[i]] === 'undefined') {
        return false;
      }
    }
    return true;
  }

  /**
   * returns an array of active objects
   *
   * @param {String} layer If provided, this will only return objects from
   * the specified layer
   * @return {OmniObject[]} all the objects in the canvas (or all
   * from specified layer)
   */
  getObjects(layer = null) {
    function orderByZ(a, b) {
      if (a.z < b.z) {
        return -1;
      }
      if (a.z > b.z) {
        return 1;
      }
      return 0;
    }

    this.params.objects.sort(orderByZ);
    if (layer) {
      return this.params.objects.filter(a => {
        if (a.layer === layer) {
          return 1;
        }
        return 0;
      });
    }
    return this.params.objects;
  }

  /**
   * Lerp (linear interpolation) is used for smoothly animating a property
   * from one value to another, across a duration
   *
   * @param {String} param the name of the value to lerp. This can include e.g. position.x
   * @param {Number} from the start value
   * @param {Number} to the end value
   * @param {Number} duration the time to lerp between the values
   * @return {void}
   */
  lerp(param, from, to, duration) {
    if (from === to) {
      return;
    }
    const lerpObj = {
      param,
      from,
      to,
      duration: duration || 0.5,
      time: Date.now(),
    };
    const l = this.params.lerpingVars.length;
    for (let i = 0; i < l; ++i) {
      if (this.params.lerpingVars[i].param === param) {
        this.params.lerpingVars[i] = lerpObj;
        return;
      }
    }
    this.params.lerpingVars.push(lerpObj);
    this.requestAnimation('applyLerping');
  }

  /**
   * Immediately cancel a lerping animation by name
   *
   * @param {String} param The name of the parameter to cancel lerping for
   * @return {void}
   */
  endLerp(param) {
    const l = this.params.lerpingVars.length;
    for (let i = 0; i < l; ++i) {
      if (this.params.lerpingVars[i].param === param) {
        this.params.lerpingVars.splice(i, 1);
        return;
      }
    }
  }

  /**
   * This is where lerp animations are applied
   * While there are active lerps, the canvas enters a 'game loop' where it
   * continuously renders one frame after another, until there are no
   * animations left. This means that animations significantly increase the
   * processing overhead of omnicanvas, so use animations sparingly.
   * Animations are particularly taxing on Safari for iOS
   *
   * @return {void}
   */
  applyLerping() {
    const frameTime = Date.now();
    let deadLerpers = [];

    this.params.lerpingVars.forEach((lerper, i) => {
      if (this.applyLerpingVariable(lerper, frameTime)) {
        deadLerpers.push(i);
      }
    });

    deadLerpers = deadLerpers.sort();
    let k = 0;

    deadLerpers.forEach(deadLerper => {
      this.params.lerpingVars.splice(deadLerper - k, 1);
      k++;
    });

    if (this.params.stage.clearFocalPointOnZoomReset) {
      const minZoom = this.params.stage.minZoom ?? 1;
      const hasScaleLerp = this.params.lerpingVars.some(
        lerper => lerper.param === 'scale'
      );

      if (!hasScaleLerp && this.params.scale <= minZoom + 0.001) {
        this.params.stage.focalPoint = null;
        this.params.stage.clearFocalPointOnZoomReset = false;
      }
    }

    this.render();

    if (this.params.lerpingVars.length) {
      this.requestAnimation('applyLerping');
    }
  }

  applyAnimation() {
    this.getObjects().forEach(object => {
      object.applyAnimation();
    });
    this.render({async: false});
  }

  /**
   * Applies a lerping variable. Returns true when the lerp has completed or false
   * if it needs to continue in the next frame.
   *
   * @param {String} lerper The variable to lerp. Uses dot notation to identify a
   * parameter
   * @param {Number} frameTime The timestamp of the current frame. This is used to
   * tell how far along the lerp we are
   *
   * @return {Boolean}
   */
  applyLerpingVariable(lerper, frameTime) {
    let dead = false;
    let lerpingObject = this.params;
    const paramSplit = lerper.param.split('.');
    const b = paramSplit.length - 1;

    for (let a = 0; a < b; ++a) {
      lerpingObject = lerpingObject[paramSplit[a]];
    }

    if (!this.params.stage.animationsEnabled) {
      lerpingObject[paramSplit[b]] = lerper.to;
      dead = true;
    } else {
      const deltaTime = (frameTime - lerper.time) / 1000;
      const percentageCompletion = deltaTime / lerper.duration;

      if (
        percentageCompletion >= 1 ||
        lerpingObject[paramSplit[b]] === lerper.to
      ) {
        lerpingObject[paramSplit[b]] = lerper.to;
        dead = true;
      } else {
        lerpingObject[paramSplit[b]] =
          lerper.from + (lerper.to - lerper.from) * percentageCompletion;
      }
    }

    return dead;
  }

  /**
   * At the end of the frame, run a function. This is primarily used for
   * processing the 'game loop' required to process lerps
   *
   * @param {String} method the name of an OmniCanvas method
   * @return {void}
   */
  requestAnimation(method) {
    if (typeof requestAnimationFrame === 'undefined') {
      this[method]();
    } else {
      if (this.animationRequests[method]) {
        return;
      }

      if (!this.animationRequests.clearAnimationRequests) {
        requestAnimationFrame(() => {
          this.clearAnimationRequests();
        });
        this.animationRequests.clearAnimationRequests = true;
      }
      requestAnimationFrame(() => {
        this[method]();
      });
      this.animationRequests[method] = true;
    }
  }

  /**
   * Cancel any active animation requests
   *
   * @return {void}
   */
  clearAnimationRequests() {
    this.animationRequests = [];
  }

  /**
   * Finds the limit for x, y translation when globaltranslation is enabled
   *
   * @param {Number} zoom the zoom level at which to test the limits
   * if not provided, will use current zoom levels
   * @return {Object}
   * @example {
   *  posX: 1,    the maximum X translation
   *  posY: 1,    the maximum Y translation
   *  negX: -1,   the minimum X translation
   *  negY: -1    the maximum Y translation
   * }
   */
  getMaxTranslation(zoom) {
    const cnvWidth = this.boundingBox.width;
    const cnvHeight = this.boundingBox.height;

    if (!zoom) {
      zoom = this.params.scale;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    this.getObjects().forEach(obj => {
      minX = Math.min(obj.position.x, minX);
      maxX = Math.max(obj.position.x + obj.width, maxX);
      minY = Math.min(obj.position.y, minY);
      maxY = Math.max(obj.position.y + obj.height, maxY);
    });

    const maxTranslation = {
      posX: (maxX - cnvWidth) * zoom + (cnvWidth / 2) * (zoom - 1),
      posY: (maxY - cnvHeight) * zoom + (cnvHeight / 2) * (zoom - 1),
      negX: minX * zoom - (cnvWidth / 2) * (zoom - 1),
      negY: minY * zoom - (cnvHeight / 2) * (zoom - 1),
    };

    Object.keys(maxTranslation).forEach(key => {
      maxTranslation[key] = Math.floor(maxTranslation[key]);
    });

    return maxTranslation;
  }

  /**
   * Adjust a global translation to meet a new maximum.
   *
   * @param {Number} zoom The level of zoom for the new global translation
   * @param {Boolean} instant If true, no animation will be applied. Leave
   * as null to allow a smooth transition
   *
   * @return {void}
   */
  adjustGlobalTranslation(zoom, instant) {
    if (!this.params.stage.globalControlsEnabled) {
      return;
    }

    const maxTranslation = this.getMaxTranslation(zoom);
    const newX = Mathf.clamp(
      this.params.globalTranslation.x,
      maxTranslation.negX,
      maxTranslation.posX
    );
    const newY = Mathf.clamp(
      this.params.globalTranslation.y,
      maxTranslation.negY,
      maxTranslation.posY
    );

    if (instant) {
      this.params.globalTranslation.x = newX;
      this.params.globalTranslation.y = newY;
    } else {
      this.lerp(
        'globalTranslation.x',
        this.params.globalTranslation.x,
        newX,
        0.175
      );

      this.lerp(
        'globalTranslation.y',
        this.params.globalTranslation.y,
        newY,
        0.175
      );
    }
  }

  /**
   * Calculates the width of the canvas
   *
   * @param {Boolean} excludeBounds If true, the width of the entire canvas
   * will be returned, else the width of the bounding box will be returned
   * instead
   * @return {Number}
   */
  getWidth(excludeBounds) {
    if (this.boundingBox?.width) {
      if (excludeBounds !== true) {
        return this.boundingBox.width;
      }
      return this.boundingBox.width - this.boundingBox.position.x * 2;
    }
    return this.renderer.width;
  }

  /**
   * Calculates the height of the canvas
   *
   * @param {Boolean} excludeBounds If true, the height of the entire canvas
   * will be returned, else the height of the bounding box will be returned
   * instead
   * @return {Number}
   */
  getHeight(excludeBounds) {
    if (this.boundingBox?.height) {
      if (excludeBounds !== true) {
        return this.boundingBox.height;
      }
      return this.boundingBox.height - this.boundingBox.position.y * 2;
    }
    return this.renderer.height;
  }

  /**
   * Calculates the aspect ratio of the canvas bounds
   *
   * @return {Number}
   */
  getAspectRatio() {
    return this.getWidth() / this.getHeight();
  }

  getPixelScale() {
    return this.renderer.pixelScale;
  }

  zoom(to, smooth = false) {
    if (smooth) {
      this.lerp('scale', this.params.scale, to, 0.25);
    } else {
      this.params.scale = to;
    }
  }

  zoomToFitBounds(padding = 0.9) {
    const bounds = this.boundingBox;
    const boundsWidth = bounds?.width ?? 0;
    const boundsHeight = bounds?.height ?? 0;

    if (!boundsWidth || !boundsHeight) {
      return this.params.scale;
    }

    const canvasWidth = this.params.stage.width;
    const canvasHeight = this.params.stage.height;
    const minZoom = this.params.stage.minZoom ?? 1;
    const maxZoom = this.params.stage.maxZoom ?? 5;

    const targetScale = Math.min(
      canvasWidth / boundsWidth,
      canvasHeight / boundsHeight
    );
    const fitScale = Mathf.clamp(targetScale * padding, minZoom, maxZoom);

    const boundsCenterX = bounds.position.x + boundsWidth / 2;
    const boundsCenterY = bounds.position.y + boundsHeight / 2;
    const canvasCenterX = canvasWidth / 2;
    const canvasCenterY = canvasHeight / 2;

    // Using bounds center directly over-corrects at higher zoom values because
    // focal translation is multiplied by (scale - 1). Scale-normalize the
    // offset so off-centre bounds don't get pulled too aggressively.
    const normalizedOffsetX = (boundsCenterX - canvasCenterX) / fitScale;
    const normalizedOffsetY = (boundsCenterY - canvasCenterY) / fitScale;

    this.params.stage.clearFocalPointOnZoomReset = false;
    this.params.stage.focalPoint = new Vector(
      canvasCenterX + normalizedOffsetX,
      canvasCenterY + normalizedOffsetY
    );
    this.lerp('scale', this.params.scale, fitScale, 0.25);
    this.params.globalTranslation = new Vector(0, 0);

    return fitScale;
  }

  resetZoom() {
    const minZoom = this.params.stage.minZoom ?? 1;

    this.params.stage.clearFocalPointOnZoomReset = true;
    this.lerp('scale', this.params.scale, minZoom, 0.25);
    this.params.globalTranslation = new Vector(0, 0);

    return minZoom;
  }
  /**
   * Determines if the browser is currently in a mobile viewport (mobile in
   * this sense refers to the width of the screen rather than the device)
   *
   * @return {Boolean}
   */
  isMobile() {
    return screen.width <= 992;
  }

  /**
   * Snaps an angle to the nearest 45 degrees, when within a certain range
   * The range is defined by the @var snapping.rotation
   *
   * @param {Number} angle An angle in degrees
   * @return {Number}
   */
  snapAngle(angle) {
    const angleTo45 = Math.abs(angle) % 45;
    if (
      angleTo45 < this.params.snapping.rotation ||
      angleTo45 > 45 - this.params.snapping.rotation
    ) {
      angle = Math.round(Math.abs(angle) / 45) * 45 * Math.sign(angle);
    }
    return angle;
  }

  /**
   * Snaps the position of an element to any active guidelines, if the
   * element is within a certain range of a guideline.
   * The guidelines are defined via this.params.activeGuidelines, and
   * the maximum distance to apply snapping is defined by
   * this.params.snapping.position
   *
   * @param {OmniObject} element The element to snap position for
   * @return {void}
   */
  snapPosition(element) {
    if (
      element.isGrouped ||
      this.activeControlObject ||
      this.mouseState !== 'down'
    ) {
      return;
    }

    let horizontalIntercept = false;
    let verticalIntercept = false;

    if (element?.id === this.activeEl?.id) {
      this.params.guidelines.forEach(guideline => {
        if (
          (guideline.direction === 'horizontal' && !horizontalIntercept) ||
          (guideline.direction === 'vertical' && !verticalIntercept)
        ) {
          if (guideline.intersectsWith(element)) {
            if (guideline.direction === 'horizontal') {
              horizontalIntercept = true;
            } else if (guideline.direction === 'vertical') {
              verticalIntercept = true;
            }

            guideline.snapElement(element);
            guideline.show();
          }
        }
      });
    }
  }

  /**
   * Centers an object in the canvas. Triggers a re-render.
   *
   * @param {OmniObject} element A staged element
   * @param {Boolean} horizontal Whether or not to center horizontally
   * @param {Boolean} vertical Whether or not to center vertically
   * @return {void}
   */
  centerObject(element, horizontal, vertical) {
    if (!element && this.activeEl) {
      element = this.activeEl;
    }

    if (!element) {
      return;
    }

    if (horizontal) {
      element.position.x = (this.boundingBox.width - element.width) / 2;
    }

    if (vertical) {
      element.position.y = (this.boundingBox.height - element.height) / 2;
    }

    this.render();
  }

  /**
   * Centers an element horizontally
   *
   * @param {OmniObject} element The staged element to center
   * @return {void}
   */
  centerHorizontal(element) {
    this.centerObject(element, true, false);
  }

  /**
   * Centers an element vertically
   *
   * @param {OmniObject} element The staged element to center
   * @return {void}
   */
  centerVertical(element) {
    this.centerObject(element, false, true);
  }

  /**
   * Stretches an element to the full width or height of the staging area
   * This behaves like the CSS 'background-size: contain', where it will
   * make the element as big as it can be, without overflowing the bounding
   * box
   *
   * @param {OmniObject} element The staged element to resize
   * @return {void}
   */
  fill(element, method = 'contain') {
    element = !element ? this.activeEl : element;

    if (!element) {
      return;
    }

    if (!element.outerBounds) {
      element.outerBounds = element.calculateOuterBounds();
    }

    const elementBoundsWidth =
      element.outerBounds.br.x - element.outerBounds.tl.x;
    const elementBoundsHeight =
      element.outerBounds.br.y - element.outerBounds.tl.y;
    const elementAspectRatio = elementBoundsWidth / elementBoundsHeight;
    const canvasAspectRatio = this.getAspectRatio();
    const originalWidth = element.width;
    const originalHeight = element.height;

    if (
      (method === 'contain' && elementAspectRatio > canvasAspectRatio) ||
      (method === 'cover' && elementAspectRatio < canvasAspectRatio)
    ) {
      // is wider than the canvas
      element.height *=
        (this.getWidth() / element.width) *
        (originalWidth / elementBoundsWidth);
      element.width = this.getWidth() * (originalWidth / elementBoundsWidth);
    } else {
      element.width *=
        (this.getHeight() / element.height) *
        (originalHeight / elementBoundsHeight);
      element.height =
        this.getHeight() * (originalHeight / elementBoundsHeight);
    }

    if (typeof element.applyScalingToChildren === 'function') {
      element.applyScalingToChildren();
    }
  }

  /**
   * Runs the fill and centerObject methods on an element
   * @see fill()
   * @see centerObject()
   *
   * @param {OmniObject} element The element to modify
   * @return {void}
   */
  fillAndCenter(element, method = 'contain') {
    this.fill(element, method);
    this.centerObject(element, true, true);
  }

  /**
   * Fills the object, while also centering it horizontally and shifting it
   * in the Y axis
   * @see fill()
   *
   * @param {OmniObject} element The element to modify
   * @param {Number} height The element will be repositioned so that the y
   * position is equal to height * the height of the bounding box. This is a
   * relative value between 0 and 1, where 0 is the very top of the bounding
   * box and 1 is the very bottom
   * @return {void}
   */
  fillAndMoveToHeight(element, height) {
    if (!element && this.activeEl) {
      element = this.activeEl;
    }
    if (!element) {
      return;
    }

    this.fill(element);
    this.centerHorizontal(element);

    /* relativeElemHeight takes angle into account, so if the object is rotated by
      90 degrees then the relative height would be equal to the elements width.
      relativeElemHeight of a square rotated by 45 degrees would be equal to the
      hypotenuse running from the bottom left corner to top right
    */
    const relativeElemHeight =
      Math.abs(element.height * Math.cos(element.angle * (Math.PI / 180))) +
      Math.abs(element.width * Math.sin(element.angle * (Math.PI / 180)));

    const deltaY =
      element.centerPoint().y - relativeElemHeight / 2 - element.position.y;

    element.position.y = Mathf.clamp(
      this.boundingBox.height * height - relativeElemHeight / 2 - deltaY,
      -deltaY,
      this.boundingBox.height
    );

    this.render();
  }

  /**
   * Detects if there is a significant amount of transparency in the target
   * image. If there are, then it is not suitable for screen print. In this
   * case, 'significant amount' means greater than 10% of the overall pixels
   *
   * @param {Image} img The image to test
   * @return {Boolean}
   */
  detectTransparency(img) {
    return hasTransparency(img);
  }

  /**
   * Changes the active layer
   *
   * @param {String} layerTag The name of the layer to make active
   * @return {boolean} True if the layer was successfully set as active
   */
  setActiveLayer(layerTag) {
    const fromLayer = this.activeLayerName;
    if (fromLayer === layerTag) {
      return true;
    }

    const layers = this.params.stage.layers;

    for (let i = 0, l = layers.length; i < l; ++i) {
      const layer = layers[i];
      if (layer.tag === layerTag) {
        this.params.stage.activeLayer = layerTag;
        this.renderer.onChangeLayer(fromLayer, layerTag);
        this.deselectActiveElement();
        this.render();
        return true;
      }
    }

    return false;
  }

  /**
   * Adds or replaces the background of a layer.
   *
   * @param {String} layerTag The name of the layer to add a background to
   * @param {HTMLImageElement} background The new background image for this layer
   * @return {void}
   */
  setLayerBackground(layerTag, background, scaleToRenderer = true) {
    this.setLayerImage(layerTag, background, 'background', scaleToRenderer);
    this.render();
  }

  /**
   * Adds or replaces the foreground of a layer.
   *
   * @param {String} layerTag The name of the layer to add a foreground to
   * @param {Image} foreground The new background image for this layer
   * @return {void}
   */
  setLayerForeground(layerTag, foreground, scaleToRenderer = true) {
    this.setLayerImage(layerTag, foreground, 'foreground', scaleToRenderer);
    this.render();
  }

  /**
   * Attaches an image to a layer
   *
   * @param {string} layerTag The name of the layer to add an image to
   * @param {HTMLImageElement} image The image to add
   * @param {string} imageName The name of the image. The image will be available at .images[imageName]
   */
  setLayerImage(layerTag, image, imageName, scaleToRenderer) {
    if (!this.renderer) {
      return;
    }

    const layer = this.getLayerByName(layerTag, true);
    if (!layer.images) {
      layer.images = {};
    }

    if (!image) {
      layer.images[imageName] = null;
      return;
    }

    if (!scaleToRenderer) {
      layer.images[imageName] = {
        image,
      };
      return;
    }

    const imgAspectRatio = image.height / image.width;
    const imgTargetHeight = imgAspectRatio * this.renderer.height;

    layer.images[imageName] = {
      image,
      x: 0,
      y: 0,
      w: this.renderer.width,
      h: imgTargetHeight,
    };
  }

  /**
   * Adds or replaces the bounds object for a layer
   *
   * @param {String} layerTag The name of the layer to add bounds to
   * @param {OmniBounds} bounds The new bounds for the layer
   * @return {void}
   */
  setLayerBounds(layerTag, bounds) {
    if (!bounds.className) {
      bounds = new OmniBounds(bounds);
    }

    if (!bounds.controller) {
      bounds.controller = this;
    }

    this.logger.log('Set layer bounds', this.logger.flags.INFO, [
      layerTag,
      bounds.position.toString(),
    ]);
    const layer = this.getLayerByName(layerTag, true);
    layer.bounds = bounds;
  }

  /**
   * Finds a layer object by name
   *
   * @param {String} activeLayer The name of the layer to find
   * @return {Object}
   */
  getLayerByName(layerName, createIfNotFound = false) {
    const layers = this.params.stage.layers;
    let activeLayerObject = null;

    layers.forEach(layer => {
      if (layer.tag === layerName) {
        activeLayerObject = layer;
      }
    });

    if (!activeLayerObject && createIfNotFound) {
      const newLength = this.params.stage.layers.push({
        tag: layerName,
        sortorder: layers.length,
      });

      activeLayerObject = this.params.stage.layers[newLength - 1];
    }

    return activeLayerObject;
  }

  createGhostLayer(
    targetLayerName,
    representativeLayerName,
    position,
    width,
    height,
    rotation = 0
  ) {
    const layer = this.getLayerByName(targetLayerName, true);

    layer.ghostLayers = layer.ghostLayers || {};

    layer.ghostLayers[representativeLayerName] = {
      position,
      width,
      height,
      rotation,
    };
  }

  /**
   * Creates an object containing all of the differences between two object
   * @example comparing {a: 1, b: 3, c: 2} against {a: 1, b: 2} would return
   * {b: 3, c: 2} because 'b' has changed and 'c' has been added. 'a' is the
   * same across both objects, so it is not returned in the new delta object
   *
   * @param {Object} obj1 The first object to compare. This is considered
   * the changed object
   * @param {Object} obj2 The second object to compare. This is considered
   * the reference object
   * @param {String[]} customFilter An array of properties to ignore in the
   * comparison
   * @return {Object}
   */
  comparePropertiesInObjects(obj1, obj2, customFilter) {
    if (!obj2) {
      return obj1;
    }

    const changes = {};

    let filteredProperties = [
      'controller',
      'cnv',
      'ctx',
      'controls',
      'outerBounds',
      'lerpingVars',
    ];
    if (customFilter) {
      filteredProperties = filteredProperties.concat(customFilter);
    }

    Object.keys(obj1).forEach(i => {
      if (filteredProperties.indexOf(i) !== -1) {
        return;
      }

      if (obj1[i] === 'NaN' || obj2[i] === 'NaN') {
        return;
      }

      const obj1PropType = typeof obj1[i];
      const obj2PropType = typeof obj2[i];

      if (obj1PropType === 'function') {
        return;
      }

      if (obj1PropType === 'object' && obj2PropType === 'object') {
        if (Array.isArray(obj1[i])) {
          changes[i] = obj1[i];
        }

        // if both properties are objects, compare the objects
        const propertyChanges = this.comparePropertiesInObjects(
          obj1[i],
          obj2[i]
        );
        if (propertyChanges && Object.keys(propertyChanges).length) {
          changes[i] = propertyChanges;
        }
      } else if (
        obj1[i] !== obj2[i] ||
        (typeof obj1[i] === 'number' && !Object.is(obj1[i], obj2[i]))
      ) {
        if (typeof obj1[i] === 'number') {
          if (i === 'id') {
            changes[i] = obj1[i];
          } else {
            changes[i] = parseFloat(obj1[i]).toFixed(2);
          }
        } else {
          changes[i] = obj1[i];
        }
      }
    });

    if (!Object.keys(changes).length) {
      return {};
    }
    return changes;
  }

  /**
   * Modifies an object by appending various properties
   *
   * @param {Object} obj The object to be modified
   * @param {Object} properties An object containing all the properties to
   * be added
   * @return {void}
   */
  applyPropertiesToObject(obj, properties) {
    Object.keys(properties).forEach(i => {
      if (typeof properties[i] === 'object') {
        this.applyPropertiesToObject(obj[i], properties[i]);
      } else {
        obj[i] = properties[i];
      }
    });
  }

  /**
   * Converts the entire OmniCanvas stage into an object. This object can be
   * used as a set of instructions to recreate the exact same stage.
   *
   * @return {any}
   */
  stageToTemplate() {
    const template = {
      version: 3,
      config: {},
      objects: {},
    };

    const propsToInclude = ['background'];

    const settingsDelta = this.comparePropertiesInObjects(
      this.params,
      this.defaultOptions
    );

    Object.keys(settingsDelta).forEach(key => {
      if (propsToInclude.includes(key)) {
        template.config[key] = settingsDelta[key];
      }
    });

    this.getObjects().forEach((object, i) => {
      template.objects[i] = object.toDelta();

      template.objects[i].originalCanvasWidth =
        this.getTemplateCanvasWidth(object.layer);
    });

    // convert the object to a string and limit all numbers to 2 decimal places
    const templateString = JSON.stringify(template).replace(
      /([0-9]+\.[0-9]{0,2})[0-9]*/g,
      '$1'
    );

    return JSON.parse(templateString);
  }

  /**
   * Take a template and convert it into OmniObject(s)
   *
   * @param {Object} template A canvas template object
   * @see stageToTemplate()
   * @param {Boolean} clear If true, delete anything that is currently in
   * the stage before adding the template items
   */
  fromTemplate(template, clear, forceToActiveLayer = false) {
    if (typeof template === 'string') {
      template = JSON.parse(template);
    }

    template = this.cleanTemplate(template);
    if (clear) {
      this.deleteObjects();
    }

    switch (template.version) {
      case 3:
      // VERSION 3: Version is functionally identical to version 2, but it may contain
      // additional metadata not present in version 2. The version change is mostly for
      // identification purposes
      // falls through
      case 2:
        this.loadTemplateConfig(template.config);

        this.objectsFromTemplate(template.objects, forceToActiveLayer);
        break;
      case 1:
      default:
        // LEGACY: Older templates only contained object data
        this.objectsFromTemplate(template, forceToActiveLayer);
        break;
    }
    this.render();
  }

  loadTemplateConfig(config) {
    Object.keys(config).forEach(group => {
      if (typeof config[group] === 'object') {
        Object.keys(config[group]).forEach(property => {
          if (
            typeof this.params[group] === 'undefined' ||
            !this.params[group]
          ) {
            this.params[group] = {};
          }
          this.params[group][property] = config[group][property];
        });
      }
    });
  }

  objectsFromTemplate(template, forceToActiveLayer = false) {
    const startZ = 1 + this.params.objects.length;

    Object.keys(template).forEach(i => {
      const templateItem = template[i];
      if (!templateItem || typeof templateItem !== 'object') {
        return;
      }

      if (templateItem.position) {
        templateItem.position = new Vector(
          templateItem.position.x || 0,
          templateItem.position.y || 0
        );
      }

      if (templateItem.renderScale) {
        templateItem.renderScale = new Vector(
          templateItem.renderScale.x || 1,
          templateItem.renderScale.y || 1
        );
      }

      if (!templateItem.z) {
        templateItem.z = 0;
      }

      templateItem.z += startZ;
      if (forceToActiveLayer) {
        templateItem.layer = this.activeLayerName;
      }
      this.loadTemplateElement(templateItem, i);
    });
  }

  /**
   * Loads an individual element from a template
   *
   * @param {Object} templateParams An OmniObject in template form
   * @see stageToTemplate()
   * @param {Int} index This is used to determine the z position of the new
   * element
   * @return {void}
   */
  loadTemplateElement(templateParams, index) {
    let object;

    templateParams.angle = parseInt(templateParams.angle, 10) || 0;

    if (templateParams.className === 'Image') {
      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        templateParams.img = img;
        object = new OmniImage(templateParams);

        // only insert the object if the renderer is ready and still exists
        if (this.renderer) {
          this.applyTemplateOptions(object, templateParams, index);
        }
      };

      if (templateParams.sourceUrl) {
        let url = templateParams.sourceUrl;
        const hostname = new URL(url, window.location.origin).hostname;
        if (
          ['images.teemill.com', 'images.podos.io'].includes(hostname) &&
          !/[?&]q=/.test(url)
        ) {
          const separator = url.includes('?') ? '&' : '?';
          url += `${separator}q=0`;
        }
        img.src = url;
      } else if (!isBase64(templateParams.img_src)) {
        img.src = `//${window.location.hostname}${templateParams.img_src}`;
      } else {
        img.src = templateParams.img_src;
      }
    } else if (this.factory) {
      object = this.factory.instantiate(
        templateParams.className,
        templateParams
      );
      this.applyTemplateOptions(object, templateParams, index);
    }
  }

  applyTemplateOptions(object, templateParams, index) {
    this.insert(object);

    if (typeof object.applyScalingToChildren === 'function') {
      object.applyScalingToChildren();
    }

    const layerWidth = this.getTemplateCanvasWidth(object.layer);

    const originalCanvasWidth =
      templateParams.originalCanvasWidth || layerWidth;
    const canvasRatio = layerWidth / originalCanvasWidth;

    object.width *= canvasRatio;
    object.height *= canvasRatio;
    if (object.maxWidth) {
      object.maxWidth *= canvasRatio;
    }

    object.position = new Vector(
      object.position.x * canvasRatio,
      object.position.y * canvasRatio
    );

    if (object.className === 'Text') {
      object.fontSize *= canvasRatio;
      object.letterSpacing *= canvasRatio;
    }

    if (!object.z) {
      object.z = index;
    }

    this.render();
  }

  getTemplateCanvasWidth(layerName = this.activeLayerName) {
    const layer =
      this.getLayerByName(layerName) ??
      this.activeLayer ??
      this.params.stage.layers[0];

    return layer?.bounds?.width ?? this.getWidth();
  }

  /**
   * Removes any excess from a template. Can be seen as a minifier for
   * templates
   *
   * @param {Object} template The template to be cleaned
   * @return {Object}
   */
  cleanTemplate(template) {
    Object.keys(template).forEach(t => {
      if (template[t] === null) {
        return;
      }

      if (!isNaN(template[t]) && typeof template[t] !== 'boolean') {
        if (!(typeof template[t] === 'string' && template[t][0] === '0')) {
          template[t] = parseFloat(template[t]);
        }
      }

      // if the string is numeric and doesn't have a leading zero (excluding values that are 0.x), convert it to a number
      if (
        typeof template[t] === 'string' &&
        template[t].match(/^(?:0\.[0-9]+|[1-9][0-9]*(?:\.[0-9]+)?)$/)
      ) {
        template[t] = parseFloat(template[t]);
      }

      if (typeof template[t] === 'object') {
        template[t] = this.cleanTemplate(template[t]);
      }

      if (template[t] === 'NaN') {
        template[t] = null;
      }
    });
    return template;
  }

  /**
   * Checks if the current web browser supports the experimental image
   * format .webp
   * If available, .webp will be used as the default file format over .png
   * in some situations.
   *
   *
   * @return {Boolean}
   */
  hasWebpSupport() {
    return SierraCanvas.browserSupportsWebP();
  }

  /**
   * Update the canvas with a new bounds object
   *
   * @param {OmniBounds} newBounds The new bounds object
   * @return {void}
   */
  changeBounds(newBounds) {
    const oldBounds = {
      width: this.boundingBox?.width,
      height: this.boundingBox?.height,
      pixelsPerCm: this.boundingBox?.pixelsPerCm,
    };

    this.logger.log('bounds changed', this.logger.flags.INFO, [
      newBounds.position?.toString(),
    ]);

    newBounds.init(this);

    if (
      oldBounds.width &&
      oldBounds.height &&
      newBounds.width &&
      newBounds.height
    ) {
      this.repositionObjectsToBounds(oldBounds, newBounds);
    }

    this.render();
  }

  repositionObjectsToBounds(from, to, layerKey) {
    const deltaWidth = to.width / from.width;
    const deltaHeight = to.height / from.height;
    const retainPhysicalDimensions =
      this.params?.object?.retainPhysicalDimensions;
    const canRetainPhysicalDimensions =
      retainPhysicalDimensions && from?.pixelsPerCm && to?.pixelsPerCm;

    const scaleRatio = canRetainPhysicalDimensions
      ? to.pixelsPerCm / from.pixelsPerCm
      : deltaWidth;

    this.getObjects(layerKey).forEach(object => {
      object.position.x *= deltaWidth;
      object.position.y *= deltaHeight;
      object.width *= scaleRatio;
      if (typeof object.maxWidth === 'number') {
        object.maxWidth *= scaleRatio;
      }
      object.height *= scaleRatio;
      if (typeof object.fontSize === 'number') {
        object.fontSize *= scaleRatio;
      }
      if (typeof object.applyScalingToChildren === 'function') {
        object.applyScalingToChildren();
      }
    });
  }

  /**
   * Deselects the active element and returns it
   *
   * @return {OmniObject}
   */
  deselectActiveElement() {
    const activeElement = this.activeEl;

    if (this.activeEl) {
      this.activeEl.deselect();
    }
    this.activeEl = undefined;

    return activeElement;
  }

  /**
   * If this OmniCanvas instance has a callback function assigned to
   * onElementAdded, trigger it when an element is added to the canvas
   *
   * @param {OmniObject} activeEl The object that has been added
   * @return {void}
   */
  onElementAdded(activeEl) {
    if (typeof this.params.onElementAdded === 'function') {
      this.params.onElementAdded(activeEl);
    }

    this.eventBus?.emit('element-added', activeEl);
  }

  /**
   * If this OmniCanvas instance has a callback function assigned to
   * onElementDeleted, trigger it when an element is deleted from the canvas
   *
   * @param {OmniObject} activeEl The object that has been deleted
   * @return {void}
   */
  onElementDeleted(activeEl) {
    if (typeof this.params.onElementDeleted === 'function') {
      this.params.onElementDeleted(activeEl);
    }

    this.eventBus?.emit('element-deleted', activeEl);
  }

  /**
   * If this OmniCanvas instance has a callback function assigned to
   * onElementSelected, trigger it when an element is selected
   *
   * @param {OmniObject} activeEl The object that has been selected
   * @return {void}
   */
  onElementSelected(activeEl) {
    if (typeof this.params.onElementSelected === 'function') {
      this.params.onElementSelected(activeEl);
    }

    this.eventBus?.emit('element-selected', activeEl);
  }

  /**
   * If this OmniCanvas instance has a callback function assigned to
   * onElementDeselected, trigger it when an element is deselected
   *
   * @param {OmniObject} activeEl The object that has been deselected
   * @return {void}
   */
  onElementDeselected(activeEl) {
    if (typeof this.params.onElementDeselected === 'function') {
      this.params.onElementDeselected(activeEl);
    }

    this.eventBus?.emit('element-deselected', activeEl);
  }

  /**
   * If this OmniCanvas instance has a callback function assigned to
   * onElementActivated, trigger it when an element is activated
   * 'activated' could mean when a text element enters edit mode
   *
   * @param {OmniObject} activeEl The object that has been activated
   * @return {void}
   */
  onElementActivated(activeEl) {
    if (typeof this.params.onElementActivated === 'function') {
      this.params.onElementActivated(activeEl);
    }

    this.eventBus?.emit('element-activated', activeEl);
  }

  /**
   * Handle a mousedown event
   *
   * @param {OmniCanvasEvent} e The event object
   * @return {void}
   */
  eventMouseDown(e) {
    this.mousePoint = e;
    if (this.activeEl) {
      this.endLerp('stage.fadeAlpha');
      this.params.stage.fadeAlpha = 0.65;
      this.activeEl.mouseDown(e);
    }
  }

  /**
   * Handle a mouseup event
   *
   * @param {OmniCanvasEvent} e The event object
   * @return {void}
   */
  eventMouseUp(e) {
    if (this.activeEl) {
      this.activeEl.mouseUp(e);
      this.snapPosition(this.activeEl);
    }
    this.lerp('stage.fadeAlpha', this.params.stage.fadeAlpha, 0.1, 0.25);
    this.render();
    this.adjustGlobalTranslation();
  }

  /**
   * Handle an object action event. This is used by OmniControl
   * objects, and an object action event could be a resize or rotate action
   *
   * @param {OmniCanvasEvent} e The event object
   * @return {void}
   */
  eventObjectAction(e) {
    const clickPoint = new OmniCanvasEvent(e, this);

    if (this.activeEl?.locked) {
      return;
    }

    if (typeof this.activeControlObject?.action === 'function') {
      this.activeControlObject.action(
        this.mousePoint,
        clickPoint,
        this.activeEl
      );
    }

    this.mousePoint = clickPoint;
  }

  /**
   * Handle a move event
   *
   * @param {OmniCanvasEvent} e The event object
   * @return {void}
   */
  eventMove(e) {
    const scale = this.params.scale;
    const mousePoint = this.mousePoint;
    const clickPoint = new OmniCanvasEvent(e, this);

    if (
      !this.activeEl ||
      !this.activeEl.customMoveFunction(mousePoint, clickPoint)
    ) {
      if (this.params.stage.globalControlsEnabled) {
        this.params.globalTranslation = this.params.globalTranslation.translate(
          (clickPoint.x - mousePoint.x) * scale,
          (clickPoint.y - mousePoint.y) * scale
        );
      } else if (!this.activeEl.locked) {
        this.activeEl.translate(
          clickPoint.x - mousePoint.x,
          clickPoint.y - mousePoint.y
        );
      }
    }

    this.render();
    this.mousePoint = clickPoint;
  }

  /**
   * Handle a pinch event, which is triggered on mobile when a user touches
   * the screen with 2 fingers and moves them together or apart
   *
   * @param {OmniCanvasEvent} e The event object
   * @return {void}
   */
  eventPinch(e) {
    if (this.params.disabledEvents.includes('pinch')) {
      return;
    }

    const touches = e.touches || e.originalEvent.touches;
    const newTouchPoints = [
      new OmniCanvasEvent(touches[0], this, false),
      new OmniCanvasEvent(touches[1], this, false),
    ];

    const touchPoints = this.touchPoints;

    if (touchPoints && touchPoints.length === 2) {
      const touchRadius = touchPoints[0].distanceTo(touchPoints[1]);
      const newTouchRadius = newTouchPoints[0].distanceTo(newTouchPoints[1]);
      this.params.scale *=
        1 + (newTouchRadius - touchRadius) / (this.renderer.width / 2);
      this.render();
    }

    this.touchPoints = newTouchPoints;
  }

  /**
   * Handle a drag start event
   *
   * @param {OmniCanvasEvent} e The event object
   * @return {void}
   */
  eventDragStart(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  /**
   * Handle a keydown event
   *
   * @param {OmniCanvasEvent} e The event object
   * @return {void}
   */
  eventKeyDown(e) {
    if (!this.preparedHistoryLog) {
      this.prepareHistoryLog();
    }
    this.activeEl.onKeyDown(e);
  }

  serialiseObjectForClipboard(object) {
    if (!object || typeof object.toDelta !== 'function') {
      return null;
    }

    const objectTemplate = this.cleanTemplate(object.toDelta());
    objectTemplate.id = null;

    return objectTemplate;
  }

  buildClipboardPayload(objectTemplate) {
    return JSON.stringify({
      type: 'omnicanvas/object',
      version: 1,
      object: objectTemplate,
    });
  }

  parseClipboardPayload(payloadText) {
    if (typeof payloadText !== 'string' || !payloadText.length) {
      return null;
    }

    try {
      const payload = JSON.parse(payloadText);
      if (
        payload?.type === 'omnicanvas/object' &&
        payload?.version === 1 &&
        payload?.object?.className
      ) {
        return payload.object;
      }
    } catch {
      return null;
    }

    return null;
  }

  isEditableShortcutTarget(target) {
    if (!target || !(target instanceof Element)) {
      return false;
    }

    if (target.isContentEditable) {
      return true;
    }

    const editableTarget = target.closest(
      'input, textarea, select, [contenteditable=""], [contenteditable="true"]'
    );

    return Boolean(editableTarget);
  }

  isElementInCanvasInteractionTree(target) {
    if (!target || !(target instanceof Element) || !this.canvasElement) {
      return false;
    }

    const interactionRoot =
      this.canvasElement.closest('.omni-canvas') || this.canvasElement;

    if (!(interactionRoot instanceof Element)) {
      return false;
    }

    return interactionRoot === target || interactionRoot.contains(target);
  }

  shouldHandleClipboardShortcut(e) {
    const eventTarget = e?.target;

    if (this.isEditableShortcutTarget(eventTarget)) {
      return false;
    }

    const activeTarget =
      typeof document !== 'undefined' ? document.activeElement : null;

    if (this.isEditableShortcutTarget(activeTarget)) {
      return false;
    }

    const targetInCanvas = this.isElementInCanvasInteractionTree(eventTarget);
    const activeInCanvas = this.isElementInCanvasInteractionTree(activeTarget);
    const canvasHasKeyboardFocus = activeTarget === this.canvasElement;

    return (
      targetInCanvas ||
      activeInCanvas ||
      canvasHasKeyboardFocus ||
      this.mouseState === 'down'
    );
  }

  async copyActiveElementToClipboard() {
    if (!this.activeEl) {
      return false;
    }

    const objectTemplate = this.serialiseObjectForClipboard(this.activeEl);
    if (!objectTemplate) {
      return false;
    }

    const payloadText = this.buildClipboardPayload(objectTemplate);

    if (
      typeof navigator === 'undefined' ||
      !navigator.clipboard ||
      typeof navigator.clipboard.writeText !== 'function'
    ) {
      return false;
    }

    try {
      await navigator.clipboard.writeText(payloadText);
      return true;
    } catch {
      return false;
    }
  }

  async readObjectTemplateFromClipboard() {
    if (
      typeof navigator === 'undefined' ||
      !navigator.clipboard ||
      typeof navigator.clipboard.readText !== 'function'
    ) {
      return null;
    }

    try {
      const payloadText = await navigator.clipboard.readText();
      const objectTemplate = this.parseClipboardPayload(payloadText);
      return objectTemplate;
    } catch {
      return null;
    }
  }

  insertClipboardTemplateObject(objectTemplate) {
    const templateToInsert = this.cleanTemplate(
      JSON.parse(JSON.stringify(objectTemplate))
    );

    templateToInsert.id = null;
    templateToInsert.layer = this.activeLayerName;
    templateToInsert.position = {
      x: (templateToInsert.position?.x ?? 0) + 10,
      y: (templateToInsert.position?.y ?? 0) + 10,
    };

    let object = null;

    if (templateToInsert.className === 'Image') {
      object = new OmniImage(templateToInsert);
    } else if (this.factory) {
      object = this.factory.instantiate(
        templateToInsert.className,
        templateToInsert
      );
    }

    if (!object) {
      return null;
    }

    const insertedObject = this.insert(object);

    this.activeEl = insertedObject;
    this.render();

    return insertedObject;
  }

  async pasteClipboardObject() {
    const objectTemplate = await this.readObjectTemplateFromClipboard();

    if (!objectTemplate) {
      return false;
    }

    return Boolean(this.insertClipboardTemplateObject(objectTemplate));
  }

  /**
   * Handle a keyup event
   *
   * @param {OmniCanvasEvent} e The event object
   * @return {void}
   */
  async eventKeyUp(e) {
    let preventHistoryLog = false;

    if (!this.activeEl || !this.activeEl.onKeyUp(e)) {
      const modifierPressed = e.ctrlKey || e.metaKey;

      if (modifierPressed) {
        if (e.code === 'KeyC') {
          if (!this.shouldHandleClipboardShortcut(e)) {
            return;
          }
          await this.copyActiveElementToClipboard();
        } else if (e.code === 'KeyV' && !e.defaultPrevented) {
          if (!this.shouldHandleClipboardShortcut(e)) {
            return;
          }
          const hasPasted = await this.pasteClipboardObject();
          if (hasPasted) {
            e.preventDefault();
          }
        } else if (e.code === 'KeyZ') {
          this.undo();
          preventHistoryLog = true;
        } else if (e.code === 'KeyY') {
          this.redo();
          preventHistoryLog = true;
        }
      } else if (
        (e.code === 'Delete' || e.code === 'Backspace') &&
        !e.defaultPrevented
      ) {
        e.preventDefault();
        if (this.activeEl) {
          this.activeEl.delete(e);
        }
      }
    }

    if (!preventHistoryLog) {
      this.commitHistoryLog();
    }
  }

  /**
   * Creates a copy of the canvas state to be committed to history
   */
  prepareHistoryLog() {
    if (!this.params.history.enabled) {
      return;
    }

    this.preparedHistoryLog = this.stageToTemplate();
  }

  /**
   * Commits a prepared history log if there is one, or the current state
   *
   * @param {Boolean} clearForward If true, this will reset the forward history log (redo log)
   */
  commitHistoryLog(clearForward = true) {
    if (!this.params.history.enabled) {
      return;
    }

    if (!this.preparedHistoryLog) {
      this.prepareHistoryLog();
    }

    const newState = this.preparedHistoryLog;

    this.preparedHistoryLog = null;

    if (this.historyLog.length) {
      const lastEvent = this.historyLog[this.historyLog.length - 1];

      if (JSON.stringify(lastEvent) === JSON.stringify(newState)) {
        return;
      }
    }

    this.historyLog.push(newState);

    if (clearForward) {
      this.forwardhistoryLog = [];
    }

    // trim history down to max length
    if (this.params.history.maximumLogs) {
      if (this.historyLog.length > this.params.history.maximumLogs) {
        this.historyLog = this.historyLog.slice(
          this.historyLog.length - this.params.history.maximumLogs
        );
      }
    }
  }

  /**
   * Removes the most recent history log and replaces it with a new one
   */
  replaceHistoryLog() {
    this.historyLog.pop();

    this.commitHistoryLog();
  }

  /**
   * Goes back to a previous history state, if there is one
   */
  undo() {
    if (!this.params.history.enabled) {
      return;
    }

    if (this.historyLog.length) {
      const lastState = this.historyLog.pop();

      this.fromHistoryState(lastState, true);
    }
  }

  /**
   * Goes forward to the next history state, if there is one
   */
  redo() {
    if (!this.params.history.enabled) {
      return;
    }

    if (this.forwardhistoryLog.length) {
      const nextState = this.forwardhistoryLog.splice();

      this.commitHistoryLog(false);

      this.fromHistoryState(nextState, true);
    }
  }

  /**
   * Loads in a history state
   *
   * @param {Object} state A history state
   */
  fromHistoryState(state) {
    state = this.cleanTemplate(state);

    if (state.config) {
      this.loadTemplateConfig(state.config);
    }

    const objectIds = [];

    Object.values(state.objects).forEach(stateObject => {
      objectIds.push(stateObject.id);

      const object = this.findObject(parseInt(stateObject.id, 10));

      if (object) {
        const original = new object.constructor();
        const currentDelta = object.toDelta();

        // any properties that are defined in the current object, but not defined
        // in the new state object must be reverted back to their original values
        Object.keys(currentDelta).forEach(key => {
          if (typeof stateObject[key] === 'undefined') {
            stateObject[key] = original[key];
          }
        });

        Object.keys(stateObject).forEach(property => {
          if (stateObject[property]) {
            this.assignPropertyToObject(
              object,
              property,
              stateObject[property]
            );
          }
        });
      }
    });

    this.getObjects().forEach(object => {
      if (!objectIds.includes(object.id)) {
        this.deleteObject(object);
      }
    });

    this.render();
  }

  assignPropertyToObject(object, property, value) {
    if (isObject(value) && isObject(object[property])) {
      Object.keys(value).forEach(prop => {
        this.assignPropertyToObject(object[property], prop, value[prop]);
      });
    } else {
      object[property] = value;
    }
  }

  /**
   * Determine if the stage is empty. The stage may be considered empty even
   * if there are active elements on the stage, because 'empty' in this
   * context means that no pixel inside the bounds area has any alpha value
   *
   * @return {Boolean}
   */
  stageIsEmpty() {
    const activeLayerObjects = this.getObjects(this.activeLayerName);
    if (!activeLayerObjects.length) {
      return true;
    }

    const imgData = this.export('imageData');
    const data = imgData.data;
    for (let i = 0, l = data.length; i < l; i += 4) {
      if (data[i + 3]) {
        return false;
      }
    }
    return true;
  }

  countUniqueColours() {
    return this.renderer.countUniqueColours();
  }

  destroy() {
    this.renderer?.destroy();
    this.renderer = null;

    const objects = [...this.getObjects()];

    objects.forEach(object => {
      object.delete();
    });

    this.deleteObject(this.boundingBox);

    this.params.stage.layers.forEach(layer => {
      if (layer.bounds) {
        this.deleteObject(layer.bounds);
      }
    });

    this.factory = null;

    if (this.listeners) {
      this.listeners.detach();
    }

    this.canvasElement = null;

    this.eventBus = null;
  }

  logError(message, level = 'error', extraTag = null, extraData = null) {
    configureScope(scope => {
      scope.setLevel(level);

      if (extraData) {
        scope.setExtra(extraTag, extraData);
      }
    });

    captureMessage(message);
  }

  stagePositionToWindowPosition(x, y, returnAsPercentage = false) {
    const stagePosition = new Vector(x, y);
    const canvas = this.canvasElement;
    const canvasRect = canvas.getBoundingClientRect();

    // translate the position by the bounds position, to get the
    // absolute coordinate relative to the top left corner
    const bounds = this.boundingBox;
    const absoluteStagePosition = stagePosition.translate(
      bounds.position.x,
      bounds.position.y
    );

    // determine how large the canvas is rendered, relative to its
    // actual width
    const xRatio = canvasRect.width / canvas.width;
    const yRatio = canvasRect.height / canvas.height;

    if (returnAsPercentage) {
      return {
        x: 100 * ((absoluteStagePosition.x * xRatio) / canvasRect.width),
        y: 100 * ((absoluteStagePosition.y * yRatio) / canvasRect.height),
      };
    }

    return {
      x: absoluteStagePosition.x * xRatio,
      y: absoluteStagePosition.y * yRatio,
    };
  }
}
